import { useEffect, useMemo, useState } from "react";

import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  ViewButton,
  EditButton,
  DeleteButton,
} from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { PennField, PennInput } from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";
import { useDeleteTask, useTasksQueue } from "@/hooks/useTasks";
import type { ListTasksQuery, TaskFlowType, TaskSummary } from "@/api/tasks";
import { TaskDetailModal } from "@/components/admin/tasks/TaskDetailModal";
import { TaskEditModal } from "@/components/admin/tasks/TaskEditModal";

type StatusFilter = "all" | "open" | "quoted";
type FlowFilter = "all" | TaskFlowType;

const SELECT_CLASS =
  "h-10 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-body text-blue_grey";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export const TasksPage = () => {
  const role = useAppSelector((s) => s.auth.user?.role ?? null);
  const canDelete = !!role?.canManageUsers;

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [flowFilter, setFlowFilter] = useState<FlowFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskSummary | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPageIndex(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo<ListTasksQuery>(
    () => ({
      limit: pageSize,
      offset: pageIndex * pageSize,
      ...(flowFilter !== "all" ? { flowType: flowFilter } : {}),
      ...(statusFilter === "open"
        ? { quoted: "false" as const }
        : statusFilter === "quoted"
          ? { quoted: "true" as const }
          : {}),
      ...(search ? { search } : {}),
    }),
    [pageIndex, pageSize, flowFilter, statusFilter, search],
  );

  const { data, isLoading, isFetching, isError } = useTasksQueue(params);
  const deleteTask = useDeleteTask();

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasNext = (pageIndex + 1) * pageSize < total;

  const cols: ColDef<TaskSummary>[] = [
    { header: "Updated", render: (t) => fmtDate(t.updatedAt) },
    {
      header: "Status",
      render: (t) => (
        <span
          className={`px-2 py-0.5 text-small-medium rounded border ${
            t.quotedAt
              ? "bg-penn-100 text-penn-700 border-penn-400"
              : "bg-green-100 text-green-700 border-green-300"
          }`}
        >
          {t.quotedAt ? "QUOTED" : "OPEN"}
        </span>
      ),
    },
    {
      header: "Requested by",
      // POC parity: user NAME with the email underneath.
      render: (t) => {
        const name =
          [t.workingForFirstName, t.workingForLastName]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          [t.requestedByFirstName, t.requestedByLastName]
            .filter(Boolean)
            .join(" ")
            .trim();
        const email = t.workingForEmail ?? t.requestedByEmail ?? null;
        const primary = name || email || t.createdBy || "—";
        return (
          <div className="min-w-0">
            <div className="truncate">{primary}</div>
            {email && email !== primary && (
              <div className="truncate text-blue_grey3 text-small">
                {email}
              </div>
            )}
          </div>
        );
      },
    },
    { header: "Company", render: (t) => t.requestedByCompanyName ?? "—" },
    { header: "Flow", render: (t) => t.flowType ?? "—" },
    { header: "Task ID", render: (t) => t.taskId.slice(0, 8) },
    {
      header: "Actions",
      render: (t) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => setOpenTaskId(t.taskId)} />
          <EditButton onClick={() => setEditTaskId(t.taskId)} />
          {canDelete && <DeleteButton onClick={() => setDeleteTarget(t)} />}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Tasks"
        subtitle="Region-scoped quotation / support tasks."
      />

      <div className="mb-4 grid grid-cols-1 sm:grid-cols-[1fr_180px_180px] gap-3">
        <PennField label="Search">
          <PennInput
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Requester name, email, or company…"
          />
        </PennField>
        <PennField label="Status">
          <select
            className={SELECT_CLASS}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              setPageIndex(0);
            }}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="quoted">Quoted</option>
          </select>
        </PennField>
        <PennField label="Flow">
          <select
            className={SELECT_CLASS}
            value={flowFilter}
            onChange={(e) => {
              setFlowFilter(e.target.value as FlowFilter);
              setPageIndex(0);
            }}
          >
            <option value="all">All</option>
            <option value="engineer">Engineer</option>
            <option value="sales">Sales</option>
          </select>
        </PennField>
      </div>

      <AdminTable
        columns={cols}
        rows={rows}
        keyExtractor={(t) => t.taskId}
        loading={isLoading}
        emptyMessage={isError ? "Could not load tasks." : "No tasks found."}
      />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />

      {openTaskId && (
        <TaskDetailModal
          taskId={openTaskId}
          onClose={() => setOpenTaskId(null)}
        />
      )}

      {editTaskId && (
        <TaskEditModal
          taskId={editTaskId}
          onClose={() => setEditTaskId(null)}
        />
      )}

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteTask.mutate(deleteTarget.taskId);
        }}
        itemLabel={deleteTarget?.taskId?.slice(0, 8)}
      />
    </>
  );
};
